// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Crew Relationship Configuration
 * 
 * All tunable parameters for the crew relationship system
 */

export const crewRelationshipConfig = {
  // Relationship score thresholds
  thresholds: {
    hostile: 20,      // 0-19: HOSTILE
    distant: 40,      // 20-39: DISTANT
    neutral: 60,      // 40-59: NEUTRAL
    friendly: 80,     // 60-79: FRIENDLY
    close: 100        // 80-100: CLOSE
  },

  // Mediation settings
  mediation: {
    successChance: 0.7,           // 70% base success rate
    lowRelationshipBonus: 0.1,    // +10% if relationship < 30
    successMin: 10,               // Minimum improvement on success
    successMax: 20,               // Maximum improvement on success
    partialMin: 2,                // Minimum improvement on partial success
    partialMax: 7                 // Maximum improvement on partial success
  },

  // Counseling settings
  counseling: {
    improvementMin: 15,           // Minimum improvement
    improvementMax: 25            // Maximum improvement
  },

  // Team building settings
  teamBuilding: {
    improvementMin: 5,            // Minimum improvement per relationship
    improvementMax: 10            // Maximum improvement per relationship
  },

  // Combat bonus settings
  combatBonus: {
    excellent: 1.2,               // +20% damage at 80+ avg
    good: 1.1,                    // +10% damage at 60-79 avg
    neutral: 1.0,                 // No change at 40-59 avg
    poor: 0.9,                    // -10% damage at 20-39 avg
    critical: 0.8                 // -20% damage at 0-19 avg
  },

  // Efficiency bonus settings
  efficiencyBonus: {
    excellent: 1.15,              // +15% efficiency at 80+ avg
    good: 1.05,                   // +5% efficiency at 60-79 avg
    neutral: 1.0,                 // No change at 40-59 avg
    poor: 0.95,                   // -5% efficiency at 20-39 avg
    critical: 0.85                // -15% efficiency at 0-19 avg
  },

  // Mission impact settings
  missionImpact: {
    successMin: 5,                // Minimum improvement on success
    successMax: 10,               // Maximum improvement on success
    failureMin: -6,               // Maximum penalty on failure
    failureMax: -2                // Minimum penalty on failure
  },

  // Personality conflict settings
  personalityConflict: {
    threshold: 70,                // Difference threshold for conflict
    penaltyPerType: -3,           // Penalty per conflict type
    types: [
      'social_style',             // Introvert vs Extrovert
      'attitude',                 // Serious vs Humorous
      'risk_tolerance'            // Cautious vs Bold
    ]
  },

  // Time decay settings
  timeDecay: {
    intervalMinutes: 5,           // How often decay runs (minutes)
    amount: 1                     // Amount to drift toward neutral
  },

  // Random event settings
  randomEvents: {
    checkIntervalSeconds: 10,     // Check every N seconds during warp
    triggerChance: 0.1,           // 10% chance per check
    events: [
      { type: 'shared_meal', change: 5, message: 'shared a meal together' },
      { type: 'argument', change: -8, message: 'had an argument' },
      { type: 'helped', change: 10, message: 'helped each other out' },
      { type: 'disagreement', change: -5, message: 'had a disagreement' },
      { type: 'bonding', change: 12, message: 'bonded over shared interests' },
      { type: 'misunderstanding', change: -6, message: 'had a misunderstanding' }
    ]
  },

  // History settings
  history: {
    maxEntries: 50                // Maximum history entries per relationship
  }
};

export default crewRelationshipConfig;
